import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureService } from '@sb/shared/feature';
import { filter, take } from 'rxjs/operators';

import { CustomerIOService } from './customer-io.service';
import { FEATURE_FLAG_QUERY_PARAMS, FeatureFlagQueryParam } from './feature-flag-query-param.config';

/**
 * How long an enabled flag's query param lingers in the URL before it's
 * removed. The param is a one-off signal (e.g. to trigger an in-app message),
 * not persistent state, so we clear it shortly after applying it.
 */
const QUERY_PARAM_REMOVAL_DELAY_MS = 1000;

/**
 * Watches the feature flags declared in {@link featureFlagQueryParamMap} and,
 * once a flag is enabled, adds its query param to the current URL (without
 * pushing a history entry), then removes it again after
 * {@link QUERY_PARAM_REMOVAL_DELAY_MS}. Each match also reports a
 * `Feature Flag <name> enabled` event to Customer.io.
 *
 * Instantiated eagerly from `AuthenticatedComponent`.
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagQueryParamService {
  #router = inject(Router);
  #feature = inject(FeatureService);
  #customerIo = inject(CustomerIOService);
  #mappings = inject(FEATURE_FLAG_QUERY_PARAMS);

  constructor() {
    this.#mappings.forEach((mapping) => this.#watch(mapping));
  }

  #watch(mapping: FeatureFlagQueryParam): void {
    this.#feature
      .isEnabled$(mapping.featureFlag)
      .pipe(
        filter((enabled) => enabled),
        take(1),
      )
      .subscribe(() => this.#applyQueryParam(mapping));
  }

  #applyQueryParam({ featureFlag, key, value }: FeatureFlagQueryParam): void {
    this.#router
      .navigate([], {
        queryParams: { [key]: value },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .then((navigated) => {
        if (!navigated) {
          return;
        }

        this.#customerIo.track(`Feature Flag ${featureFlag} enabled`);

        setTimeout(() => this.#removeQueryParam(key), QUERY_PARAM_REMOVAL_DELAY_MS);
      })
      .catch(() => undefined);
  }

  #removeQueryParam(key: string): void {
    this.#router
      .navigate([], {
        queryParams: { [key]: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      })
      .catch(() => undefined);
  }
}
